import Vue from 'vue';
import Router from 'vue-router';
import Meta from 'vue-meta';

// Keep only critical above-the-fold components as synchronous imports
import Home from '@/components/views/Home';
import NotFound from '@/components/views/NotFound';

import store from '@/store';

// Convert all other components to dynamic imports for code splitting
const Contact = () => import('@/components/views/Contact');
const About = () => import('@/components/views/About');
const Privacy = () => import('@/components/views/Privacy');
const Terms = () => import('@/components/views/Terms');
const Login = () => import('@/components/views/Login');
const Register = () => import('@/components/views/Register');
const RegisterOk = () => import('@/components/views/RegisterOk');
const RegisterArtistOk = () => import('@/components/views/RegisterArtistOk');
const ArtistRegister = () => import('@/components/views/ArtistRegister');
const Gallery = () => import('@/components/views/Gallery');
const Love = () => import('@/components/views/Love');
const Artists = () => import('@/components/views/Artists');
const Piece = () => import('@/components/views/Piece');
// Account
const Account = () => import('@/components/views/Account');
const AccountProfile = () => import('@/components/views/AccountProfile');
const AccountAddress = () => import('@/components/views/AccountAddress');
const AccountRentals = () => import('@/components/views/AccountRentals');
const AccountOrders = () => import('@/components/views/AccountOrders');
const Order = () => import('@/components/views/Order');
const OrderOk = () => import('@/components/views/OrderOk');
const KeepOkay = () => import('@/components/views/KeepOkay');
const OrderNotOk = () => import('@/components/views/OrderNotOk');
const ConfirmEmail = () => import('@/components/views/ConfirmEmail');
const Rental = () => import('@/components/views/Rental');
const Checkout = () => import('@/components/views/Checkout');
const Cart = () => import('@/components/views/Cart');
const Rent = () => import('@/components/views/Rent');
const PaymentSuccess = () => import('@/components/views/PaymentSuccess');
const PaymentFailed = () => import('@/components/views/PaymentFailed');
const ResetPassword = () => import('@/components/views/ResetPassword');
// Help
const Help = () => import('@/components/views/Help');
const HelpPackaging = () => import('@/components/views/articles/Packaging');
const Rentals = () => import('@/components/views/Rentals');
const CorporateRentals = () => import('@/components/views/CorporateRentals');
// Legal
const Legal = () => import('@/components/views/Legal');

Vue.use(Router);
Vue.use(Meta);

const router = new Router({
  mode: 'history',
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      meta: {
        title: 'Home',
        layout: 'layout-basic',
      },
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
      meta: {
        title: 'Login',
        layout: 'layout-app',
      },
    },
    {
      path: '/register',
      name: 'register',
      component: Register,
      meta: { layout: 'layout-app' },
    },
    {
      path: '/register-ok',
      name: 'RegisterOk',
      component: RegisterOk,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/register-artist-ok',
      name: 'RegisterArtistOk',
      component: RegisterArtistOk,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/artist-register',
      name: 'artist-register',
      component: ArtistRegister,
      meta: { layout: 'layout-app' },
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPassword,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/account',
      component: Account,
      meta: {
        layout: 'layout-basic',
        authRequired: true,
      },
      children: [
        {
          path: 'profile',
          name: 'AccountProfile',
          component: AccountProfile,
          meta: {
            layout: 'layout-basic',
            authRequired: true,
          },
        },
        {
          path: 'address',
          name: 'AccountAddress',
          component: AccountAddress,
          meta: {
            layout: 'layout-basic',
            authRequired: true,
          },
        },
        {
          path: 'orders',
          name: 'AccountOrders',
          component: AccountOrders,
          meta: {
            layout: 'layout-basic',
            authRequired: true,
          },
        },
        {
          path: 'rentals',
          name: 'AccountRentals',
          component: AccountRentals,
          meta: {
            layout: 'layout-basic',
            authRequired: true,
          },
        },
      ],
    },
    {
      path: '/cart',
      name: 'cart',
      component: Cart,
      meta: { layout: 'layout-basic', authRequired: true },
    },
    {
      path: '/rent',
      name: 'rent',
      component: Rent,
      meta: {
        layout: 'layout-basic',
        authRequired: true,
      },
    },
    {
      path: '/checkout/:type',
      name: 'checkout',
      component: Checkout,
      meta: {
        layout: 'layout-app',
        authRequired: true,
      },
    },
    {
      path: '/contact',
      name: 'contact',
      component: Contact,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/about',
      name: 'about',
      component: About,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/help',
      name: 'help',
      component: Help,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/legal',
      name: 'legal',
      component: Legal,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/help/packaging',
      name: 'HelpPackaging',
      component: HelpPackaging,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/rentals',
      name: 'rentals',
      component: Rentals,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/corporate-rentals',
      name: 'CorporateRentals',
      component: CorporateRentals,
      meta: { layout: 'layout-basic' },
    },
    {
      // path: '/gallery/:form?/:medium?/:style?/:subject?/:artist_id?',
      path: '/gallery',
      name: 'gallery',
      component: Gallery,
      meta: {
        layout: 'layout-basic',
        title: 'Art Gallery',
      },
    },
    {
      path: '/love',
      name: 'love',
      component: Love,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/artists',
      name: 'artists',
      component: Artists,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/art/:pieceId/:pieceTitle',
      name: 'Piece',
      component: Piece,
      meta: {
        layout: 'layout-basic',
      },
    },
    {
      path: '/account/orders/:orderId',
      name: 'order',
      component: Order,
      meta: {
        layout: 'layout-basic',
        authRequired: true,
      },
    },
    {
      path: '/account/rentals/:rentalId',
      name: 'rental',
      component: Rental,
      meta: {
        layout: 'layout-basic',
        authRequired: true,
      },
    },
    // {
    //   path: '/gallery/:pieceTitle',
    //   name: 'Piece',
    //   component: Piece,
    //   props: route => ({
    //     // user: userData,
    //     ...route.params,
    //   }),
    //   meta: {
    //     layout: 'layout-basic',
    //   },
    // },
    {
      path: '/order-ok',
      name: 'OrderOk',
      component: OrderOk,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/keep-ok',
      name: 'KeepOkay',
      component: KeepOkay,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/confirm-email',
      name: 'ConfirmEmail',
      component: ConfirmEmail,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/order-not-ok',
      name: 'OrderNotOk',
      component: OrderNotOk,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: Privacy,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/terms',
      name: 'terms',
      component: Terms,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '*',
      name: 'notfound',
      component: NotFound,
      meta: { layout: 'layout-basic' },
    },
    {
      path: '/payment-success',
      name: 'paymentsuccess',
      component: PaymentSuccess,
    },
    {
      path: '/payment-failed',
      name: 'paymentfailed',
      component: PaymentFailed,
    },
  ],
});

// eslint-disable-next-line consistent-return
router.beforeEach((to, from, next) => {
  if (from.name !== 'login') {
    store.commit('account/setFromUrl', from.path);
  }
  // Set view layout
  store.commit('layout/setLayout', to.meta.layout);
  // redirect to login page if not logged in and trying to access a restricted page
  const authRequired = to.matched.some(record => record.meta.authRequired);
  const loggedIn = localStorage.getItem('user');

  if (!loggedIn && authRequired) {
    // Save url user was going to, to continue there after login
    store.commit('account/setToUrl', to.path);
    return next('/login');
  }
  // Set page title
  document.title = to.name;
  next();
});


export default router;



// WEBPACK FOOTER //
// ./src/router/index.js